import React, { useEffect } from 'react';
import heroImage from '/assets/landingmapview-v2.png';
import { RoddoFishIcon } from './icons';

type LandingPageProps = {
  onOpenApp: () => void;
  onSignIn: () => void;
  onOpenTerms?: () => void;
  onOpenPrivacy?: () => void;
  termsHref?: string;
  privacyHref?: string;
};

type FeatureIconKey = 'bigblue' | 'camera' | 'fishiq' | 'crew' | 'trophy';

const FeatureIcon = ({ icon }: { icon: FeatureIconKey }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: 'h-8 w-8 shrink-0',
  };

  if (icon === 'bigblue') {
    return (
      <RoddoFishIcon
        mode="png"
        animated={false}
        className="block object-contain"
        style={{
          width: 64,
          height: 34,
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: 'contain',
          objectPosition: 'center',
        }}
      />
    );
  }

  if (icon === 'camera') {
    return (
      <svg {...commonProps}>
        <path d="M5 7h3l1.2-2h5.6L16 7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
        <circle cx="12" cy="13" r="3.2" />
      </svg>
    );
  }

  if (icon === 'fishiq') {
    return (
      <RoddoFishIcon
        mode="png"
        animated={false}
        className="block object-contain"
        style={{
          width: 56,
          height: 30,
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: 'contain',
          objectPosition: 'center',
        }}
      />
    );
  }

  if (icon === 'crew') {
    return (
      <svg {...commonProps}>
        <circle cx="9" cy="9" r="3" />
        <circle cx="17" cy="10" r="2.5" />
        <path d="M3.5 19c.7-3.2 2.6-4.8 5.5-4.8s4.8 1.6 5.5 4.8" />
        <path d="M14.5 15.2c2.9-.5 4.9.8 6 3.8" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M8 4h8v3.5c0 3.3-1.5 5.5-4 6.5-2.5-1-4-3.2-4-6.5V4Z" />
      <path d="M8 6H4.5v1.5c0 2.4 1.4 3.9 4 4.3" />
      <path d="M16 6h3.5v1.5c0 2.4-1.4 3.9-4 4.3" />
      <path d="M12 14v3.5" />
      <path d="M8.5 20h7" />
      <path d="M10 17.5h4" />
    </svg>
  );
};

const featureItems = [
  { title: 'Big Blue', body: 'Explore the water.', icon: 'bigblue' as const },
  { title: "Capt'n's Log", body: 'Remember every catch.', icon: 'camera' as const },
  { title: 'Roddo Reads', body: 'Read the water smarter.', icon: 'fishiq' as const },
  { title: 'Crew', body: 'Fish with people you trust.', icon: 'crew' as const },
  { title: 'Tournaments', body: 'Compete and prove catches.', icon: 'trophy' as const },
] as const;

export default function LandingPage({
  onOpenApp,
  onSignIn,
  onOpenTerms,
  onOpenPrivacy,
  termsHref = '/terms',
  privacyHref = '/privacy',
}: LandingPageProps) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const snapshot = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyMinHeight: body.style.minHeight,
      rootOverflow: root?.style.overflow ?? '',
      rootHeight: root?.style.height ?? '',
      rootMinHeight: root?.style.minHeight ?? '',
    };

    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.minHeight = '100vh';

    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
      root.style.minHeight = '100vh';
    }

    return () => {
      html.style.overflow = snapshot.htmlOverflow;
      html.style.height = snapshot.htmlHeight;
      body.style.overflow = snapshot.bodyOverflow;
      body.style.height = snapshot.bodyHeight;
      body.style.minHeight = snapshot.bodyMinHeight;
      if (root) {
        root.style.overflow = snapshot.rootOverflow;
        root.style.height = snapshot.rootHeight;
        root.style.minHeight = snapshot.rootMinHeight;
      }
    };
  }, []);

  const LegalLink = ({
    label,
    href,
    onClick,
  }: {
    label: string;
    href: string;
    onClick?: () => void;
  }) =>
    onClick ? (
      <button type="button" onClick={onClick} className="transition-opacity hover:opacity-65">
        {label}
      </button>
    ) : (
      <a href={href} className="transition-opacity hover:opacity-65">
        {label}
      </a>
    );

  return (
    <div className="min-h-screen bg-[#F7F2E8] text-[#30271E] dark:bg-[#090D18] dark:text-[#F4F6FA]">
      <header className="fixed inset-x-0 top-0 z-[100] w-full border-b border-black/[.07] bg-[#FFFCF7]/94 backdrop-blur-xl dark:border-white/[.08] dark:bg-[#0D1322]/94">
        <div className="mx-auto flex min-h-[68px] w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onOpenApp}
            className="font-black leading-none text-[#10284A] transition-opacity hover:opacity-80 dark:text-[#F4F6FA]"
            style={{
              fontWeight: 950,
              fontSize: 'clamp(30px, 2.35vw, 42px)',
              letterSpacing: '-0.055em',
            }}
            aria-label="Open Roddo"
          >
            Roddo
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSignIn}
              className="rounded-[11px] px-4 py-2.5 text-sm font-black text-[#44505A] transition hover:bg-black/[.035] dark:text-[#AEB8C9] dark:hover:bg-white/[.05]"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={onOpenApp}
              className="rounded-[11px] bg-[#2F80ED] px-4 py-2.5 text-sm font-black text-white shadow-[0_5px_14px_rgba(47,128,237,.16)] transition hover:brightness-105 dark:bg-[#7057E8] dark:shadow-[0_6px_16px_rgba(112,87,232,.18)]"
            >
              Enter Roddo
            </button>
          </div>
        </div>
      </header>

      <main className="pt-[68px]">
        <section className="mx-auto w-full max-w-6xl px-3 pb-10 pt-10 sm:px-5 sm:pb-12 sm:pt-12 min-[700px]:pt-16 min-[1100px]:px-6 min-[1100px]:pb-14 min-[1100px]:pt-30">
          <div className="relative rounded-[22px] border border-black/[.08] bg-[#FFFCF7] shadow-[0_14px_34px_rgba(74,58,42,.08)] dark:border-white/[.09] dark:bg-[#121A2B] dark:shadow-[0_16px_38px_rgba(0,0,0,.24)]">
            <div className="grid min-[1100px]:grid-cols-[0.72fr_1.28fr] min-[1100px]:items-center xl:grid-cols-[0.70fr_1.30fr]">
              <div className="flex flex-col justify-center px-6 py-8 sm:px-8 sm:py-10 min-[700px]:px-10 min-[700px]:py-11 min-[1100px]:px-10 min-[1100px]:py-11 xl:px-12 xl:py-12">
                <div className="text-[11px] font-black uppercase tracking-[0.2em] text-[#2F80ED] dark:text-[#A99AF7]">
                  Fishing intelligence + community
                </div>

                <h1 className="mt-4 max-w-xl text-[38px] font-black leading-[0.98] tracking-[-0.045em] sm:text-[44px] min-[700px]:text-[48px] min-[1100px]:text-[52px] xl:text-[58px]">
                  Explore. Log. Connect.
                </h1>

                <p className="mt-4 max-w-xl text-[15px] font-semibold leading-6 text-[#6E6255] sm:mt-5 sm:text-base sm:leading-7 lg:text-[17px] dark:text-[#AEB8C9]">
                  Your waters, catches, fishing intelligence, crew, and competition — together in one fishing home.
                </p>

                <div className="mt-6 flex flex-col gap-3 xs:flex-row sm:mt-7">
                  <button
                    type="button"
                    onClick={onOpenApp}
                    className="min-h-12 rounded-[12px] bg-[#2F80ED] px-6 text-sm font-black text-white shadow-[0_6px_16px_rgba(47,128,237,.18)] transition hover:brightness-105 dark:bg-[#7057E8] dark:shadow-[0_8px_20px_rgba(112,87,232,.20)]"
                  >
                    Enter Roddo
                  </button>
                  <button
                    type="button"
                    onClick={onSignIn}
                    className="min-h-12 rounded-[12px] border border-black/[.10] bg-[#F2EBE1] px-6 text-sm font-black text-[#30271E] transition hover:bg-[#ECE3D6] dark:border-white/[.10] dark:bg-[#182238] dark:text-[#F4F6FA] dark:hover:bg-[#1C2943]"
                  >
                    Sign in
                  </button>
                </div>

                <div className="mt-6 text-xs font-bold leading-5 text-[#8A7B6B] dark:text-[#7F8BA0]">
                  Private logs. Trusted crews. Deliberate sharing.
                </div>
              </div>

              <div className="relative border-t border-black/[.07] px-4 pb-4 pt-3 min-[700px]:px-8 min-[700px]:pb-6 min-[700px]:pt-4 min-[1100px]:border-l min-[1100px]:border-t-0 min-[1100px]:px-0 min-[1100px]:pb-0 min-[1100px]:pr-10 min-[1100px]:pt-0 xl:pr-12 dark:border-white/[.08]">
                <div className="relative mx-auto w-full min-[700px]:max-w-[780px] min-[1100px]:max-w-none">
                  <div className="relative aspect-[1672/941] w-full overflow-hidden rounded-[18px] bg-[#0D79B5] min-[1100px]:rounded-l-none min-[1100px]:rounded-r-[21px]">
                  <img
                    src={heroImage}
                    alt="Roddo Big Blue map showing Water Read, Saved Spots, Baleen Bay, Bazaar, and map tools"
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    draggable={false}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07131B]/3 via-transparent to-transparent" />
                  <div className="pointer-events-none absolute bottom-[3px] right-[8px] z-[2] whitespace-nowrap text-right text-[8px] font-black uppercase tracking-[0.08em] text-white/90 drop-shadow-[0_1px_2px_rgba(0,0,0,.85)] sm:bottom-[4px] sm:right-[10px] sm:text-[8.5px]">
                    Illustrative Roddo experience
                  </div>
                  </div>

                  <img
                    src="/assets/woodensign.png"
                  alt="Roddo — Your Pocket Fishing Buddy"
                    className="pointer-events-none absolute bottom-[-34px] left-1/2 z-10 w-[58%] max-w-[330px] -translate-x-1/2 object-contain drop-shadow-[0_10px_20px_rgba(39,28,18,.34)] sm:bottom-[-42px] sm:w-[54%] sm:max-w-[345px] min-[700px]:max-w-[360px] min-[1100px]:bottom-[-48px] min-[1100px]:w-[50%] min-[1100px]:max-w-[350px] xl:bottom-[-52px] xl:max-w-[360px]"
                    draggable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 pb-6 pt-4 sm:px-6 sm:pb-7 sm:pt-5 lg:pb-8 lg:pt-6">
          <div className="border-y border-black/[.08] py-8 dark:border-white/[.08]">
            <div className="mb-6 text-[11px] font-black uppercase tracking-[0.18em] text-[#2F80ED] dark:text-[#A99AF7]">The Roddo system</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:gap-x-8 min-[1100px]:grid-cols-5 min-[1100px]:gap-x-7">
              {featureItems.map(({ title, body, icon }) => (
                <div key={title} className="flex min-w-0 flex-col">
                  <div className="flex min-h-9 max-w-full items-center gap-2 text-[17px] font-black leading-tight tracking-[-0.02em] text-[#30271E] sm:text-[18px] lg:text-[19px] dark:text-[#F4F6FA]">
                    <span>{title}</span>
                    <span className="inline-flex min-h-8 shrink-0 items-center text-[#2F80ED] dark:text-[#A99AF7]">
                      <FeatureIcon icon={icon} />
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-semibold leading-6 text-[#6E6255] dark:text-[#AEB8C9]">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-6 lg:pb-5 lg:pt-6">
          <div className="grid items-stretch gap-4 min-[1100px]:grid-cols-2">
            <div className="flex h-full flex-col rounded-[18px] border border-black/[.08] bg-[#FFFCF7] p-6 sm:p-7 dark:border-white/[.08] dark:bg-[#121A2B]">
              <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#2F80ED] dark:text-[#A99AF7]">Private by design</div>
              <h2 className="mt-3 text-[26px] font-black leading-tight tracking-[-0.03em]">Your spots stay yours.</h2>
              <p className="mt-3 text-sm font-semibold leading-7 text-[#6E6255] dark:text-[#AEB8C9]">
                Private logs, trusted crews, and generalized sharing keep sensitive locations protected.
              </p>
            </div>

            <div className="flex h-full flex-col rounded-[18px] border border-black/[.08] bg-[#FFFCF7] p-6 sm:p-7 dark:border-white/[.08] dark:bg-[#121A2B]">
              <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#2F80ED] dark:text-[#A99AF7]">One fishing home</div>
              <h2 className="mt-3 text-[26px] font-black leading-tight tracking-[-0.03em]">Built around the way you fish.</h2>
              <p className="mt-3 text-sm font-semibold leading-7 text-[#6E6255] dark:text-[#AEB8C9]">
                Find water. Read conditions. Log catches. Keep your gear and crew together. Compete when you want. Roddo keeps the whole trip connected.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 pb-8 pt-3 sm:px-6 sm:pb-10 sm:pt-4 lg:pb-10 lg:pt-5">
          <div className="rounded-[20px] border border-[#2F80ED]/16 bg-[#EAF3FF] px-5 py-6 sm:px-7 sm:py-7 min-[700px]:flex min-[700px]:items-center min-[700px]:justify-between min-[700px]:gap-8 dark:border-[#7057E8]/18 dark:bg-[#141B31]">
            <div>
              <div className="text-2xl font-black tracking-[-0.025em]">Find your water.</div>
              <p className="mt-2 text-sm font-semibold text-[#5F6570] dark:text-[#AEB8C9]">
                Open Roddo and make the next trip part of the story.
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenApp}
              className="mt-5 min-h-12 w-full rounded-[12px] bg-[#2F80ED] px-6 text-sm font-black text-white shadow-[0_6px_16px_rgba(47,128,237,.16)] min-[700px]:mt-0 min-[700px]:w-auto dark:bg-[#7057E8]"
            >
              Enter Roddo
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-black/[.07] bg-[#FFFCF7] dark:border-white/[.08] dark:bg-[#0D1322]">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-6 text-xs font-semibold text-[#7C7063] sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:text-[#7F8BA0]">
          <div>© {new Date().getFullYear()} Roddo Technology LLC · Explore. Log. Connect.</div>
          <div className="flex flex-wrap gap-4">
            <span>support@roddo.app</span>
            <LegalLink label="Terms" href={termsHref} onClick={onOpenTerms} />
            <LegalLink label="Privacy" href={privacyHref} onClick={onOpenPrivacy} />
          </div>
        </div>
      </footer>
    </div>
  );
}
